
import React, { useMemo, useState } from 'react';
import PractitionerList from './PractitionerList';
import PractitionersFilters from './PractitionersFilters';
import PractitionerDetail from './PractitionerDetail';
import FloatingFilterButton from './FloatingFilterButton';
import { usePractitioners } from '@/hooks/use-practitioners';
import { Practitioner } from '@/types/practitioner';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface PractitionersContentProps {
  showSidebar: boolean;
  setShowSidebar: (show: boolean) => void;
  filters: {
    serviceCategories: string[];
    effectiveDomain: string;
    serviceSubcategory: string;
    distance: string;
    date: Date | undefined;
    timeSlot: string;
    sessionType: string[];
    genders: string[];
    language: string;
    issueCategory: string;
    showFreeSession: boolean;
    serviceTracks: string[];
    includeThirdPartyRating?: boolean;
    therapyModalities: string[];
  };
  setFilters: (filters: any) => void;
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  debouncedSearchQuery: string;
  userLocation: { lat: number; lng: number } | null;
  userAddress: string | null;
  locationError: string | null;
  isGettingLocation: boolean;
  isLoadingAddress: boolean;
  requestLocationAccess: () => void;
  timeSlots: string[];
  isDateDisabled: (date: Date) => boolean;
  handleCategoryChange: (categoryName: string) => void;
  city: string | null;
  state: string | null;
  country: string | null;
  timezoneDisplay: string | null;
  isApproximateLocation?: boolean;
  setManualLocation?: (lat: number, lng: number, address: string) => void;
  isGeolocationPolicyBlocked?: () => boolean;
}

const PractitionersContent: React.FC<PractitionersContentProps> = ({
  showSidebar,
  setShowSidebar,
  filters,
  setFilters,
  sortBy,
  setSortBy,
  searchQuery,
  setSearchQuery,
  debouncedSearchQuery,
  userLocation,
  userAddress,
  locationError,
  isGettingLocation,
  isLoadingAddress,
  requestLocationAccess,
  timeSlots,
  isDateDisabled,
  handleCategoryChange,
  city,
  state,
  country,
  timezoneDisplay,
  isApproximateLocation,
  setManualLocation,
  isGeolocationPolicyBlocked
}) => {
  // Add userLocation and debouncedSearchQuery to filters for filtering
  const filtersWithLocation = useMemo(() => ({
    ...filters,
    userLocation,
    searchQuery: debouncedSearchQuery
  }), [filters, userLocation, debouncedSearchQuery]);

  const { practitioners, isLoading, error, hasMore, loadMore, filtersAutoCleared } = usePractitioners(filtersWithLocation, sortBy);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const isMobile = useIsMobile();

  // Count active filters for floating button badge
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.serviceCategories.length > 0 && !filters.serviceCategories.includes('all_types')) count++;
    if (filters.effectiveDomain && filters.effectiveDomain !== 'all_domains') count++;
    if (filters.serviceSubcategory && filters.serviceSubcategory !== 'all_subcategories') count++;
    if (filters.distance !== 'any_distance') count++;
    if (filters.date) count++;
    if (filters.timeSlot !== 'any_time') count++;
    if (Array.isArray(filters.sessionType) && filters.sessionType.length > 0 && filters.sessionType.length < 2) count++;
    if (filters.genders && filters.genders.length > 0 && filters.genders.length < 3) count++;
    if (filters.language && filters.language !== 'all') count++;
    if (filters.issueCategory && filters.issueCategory !== 'all_categories') count++;
    if (filters.showFreeSession) count++;
    if (filters.serviceTracks && filters.serviceTracks.length > 0 && filters.serviceTracks.length < 2) count++;
    if (filters.therapyModalities && filters.therapyModalities.length > 0) count++;
    if (debouncedSearchQuery.trim()) count++;
    return count;
  }, [filters, debouncedSearchQuery]);

  return (
    <div className="flex flex-col lg:flex-row gap-8 animate-in fade-in-0 duration-500">{/* Fixed: removed undefined animate-slide-in and opacity-0 */}
      <PractitionersFilters
        showSidebar={showSidebar}
        filters={filters}
        setFilters={setFilters}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        userLocation={userLocation}
        userAddress={userAddress}
        locationError={locationError}
        isGettingLocation={isGettingLocation}
        isLoadingAddress={isLoadingAddress}
        requestLocationAccess={requestLocationAccess}
        timeSlots={timeSlots}
        isDateDisabled={isDateDisabled}
        handleCategoryChange={handleCategoryChange}
        city={city}
        state={state}
        country={country}
        timezoneDisplay={timezoneDisplay}
        isApproximateLocation={isApproximateLocation}
        setManualLocation={setManualLocation}
        isGeolocationPolicyBlocked={isGeolocationPolicyBlocked}
      />

      {/* Mobile Filters Sheet */}
      {isMobile && (
        <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
          <SheetContent side="bottom" className="h-[85vh] p-0">
            <SheetHeader className="p-6 border-b">
              <SheetTitle className="text-amber-700 flex items-center gap-2">
                筛选器
                {activeFiltersCount > 0 && (
                  <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-sm font-medium">
                    {activeFiltersCount}
                  </span>
                )}
              </SheetTitle>
            </SheetHeader>
            <div className="overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Search Input */}
                <div className="space-y-2">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="搜索疗愈师"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                {/* Filter components would go here - simplified for mobile */}
                <div className="space-y-4">
                  <button
                    onClick={() => {
                  setFilters({
                    serviceCategories: ["all_types"],
                    effectiveDomain: "all_domains",
                    serviceSubcategory: "all_subcategories",
                    distance: "any_distance",
                    date: undefined,
                    timeSlot: "any_time",
                    sessionType: ['virtual', 'in_person'],
                    genders: ['male', 'female', 'other'],
                    language: 'all',
                    issueCategory: 'all_categories',
                    showFreeSession: false,
                    serviceTracks: ['clinical', 'wellness'],
                    includeThirdPartyRating: true,
                    therapyModalities: [],
                  });
                      setSearchQuery("");
                    }}
                    className="w-full py-3 px-4 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors font-medium"
                  >
                    清除所有筛选
                  </button>
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Floating Filter Button for Mobile */}
      {isMobile && (
        <FloatingFilterButton
          activeFiltersCount={activeFiltersCount}
          onClick={() => setShowMobileFilters(true)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 transition-all duration-300">
        <PractitionerList 
          practitioners={practitioners}
          isLoading={isLoading}
          error={error}
          sortBy={sortBy}
          onSortChange={setSortBy}
          setShowSidebar={setShowSidebar}
          hasMore={hasMore}
          onLoadMore={loadMore}
          filters={filters}
          onFiltersChange={setFilters}
          timeSlots={timeSlots}
          isDateDisabled={isDateDisabled}
          userLocation={userLocation}
          locationError={locationError}
          city={city}
          state={state}
          country={country}
          isGettingLocation={isGettingLocation}
          isLoadingAddress={isLoadingAddress}
          timezoneDisplay={timezoneDisplay}
          isApproximateLocation={isApproximateLocation}
          requestLocationAccess={requestLocationAccess}
          filtersAutoCleared={filtersAutoCleared}
        />
      </div>
    </div>
  );
};

export default PractitionersContent;
